import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { useTransactionTrace, useTransactionTraceCloudWatch } from '@/hooks/use-admin-transactions';
import { TraceSummary } from './TraceSummary';
import { TracePhases } from './TracePhases';
import { TraceTimeline } from './TraceTimeline';
import { TraceWarnings } from './TraceWarnings';
import { TraceErrors } from './TraceErrors';
import { StateMismatchBanner } from './StateMismatchBanner';
import { CloudWatchPanel } from './CloudWatchPanel';

/**
 * Container der Trace-Ansicht (Spec 143 §5.3/§5.4).
 *
 * Lazy: Diese Komponente wird erst gemountet, wenn der Trace-Tab aktiv ist —
 * der Mount selbst ist das Gate, deshalb enabled=true. Datenquelle ist
 * ausschliesslich der BFF-Proxy auf die Facade-Trace-API.
 *
 * CloudWatch-Korrelation: Opt-in per Toggle (Trap §8.7). Liegt die
 * CW-Response vor, ist sie die fuehrende Datenquelle fuer die gesamte
 * Ansicht (Superset: Phasen tragen dann cwStatus/mismatch) — waehrend
 * die CW-Query laeuft, bleibt die Basis-Ansicht sichtbar (§5.4).
 */
export function TransactionTraceView({ transactionId }: { transactionId: string }) {
  const { t } = useTranslation('transactions');
  const { data: baseTrace, isLoading, error, refetch } = useTransactionTrace(transactionId, true);

  const [cwRequested, setCwRequested] = useState(false);
  const {
    data: cwTrace,
    isLoading: isCwLoading,
    error: cwError,
    refetch: refetchCw,
  } = useTransactionTraceCloudWatch(transactionId, cwRequested);

  // Cache-Reuse bei Tab-Remount (Trap §8.9): React Query liefert gecachte
  // CW-Daten auch bei enabled=false. Vorhandene Daten zaehlen deshalb als
  // aktiviert — sonst zeigte die Ansicht CW-Anreicherung (Phasen/Mismatches),
  // waehrend das Panel wieder den Opt-in-Toggle praesentiert (inkonsistent).
  // Es feuert dabei KEIN neuer Fetch (staleTime Infinity).
  const cwActive = cwRequested || cwTrace !== undefined;

  // CW-Response = Superset → fuehrend, sobald vorhanden
  const trace = cwTrace ?? baseTrace;

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="trace-loading">
        <Skeleton className="h-8 w-1/2 motion-reduce:animate-none" />
        <div className="grid gap-3 sm:grid-cols-3">
          <Skeleton className="h-20 motion-reduce:animate-none" />
          <Skeleton className="h-20 motion-reduce:animate-none" />
          <Skeleton className="h-20 motion-reduce:animate-none" />
        </div>
        <Skeleton className="h-32 motion-reduce:animate-none" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!trace) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        {t('trace.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="trace-view">
      <StateMismatchBanner mismatches={trace.stateMismatches ?? []} />
      <TraceWarnings warnings={trace.warnings ?? []} />
      <TraceErrors errors={trace.errors ?? []} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('trace.summary.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <TraceSummary trace={trace} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('trace.phases.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <TracePhases phases={trace.phases ?? []} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('trace.timeline.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <TraceTimeline timeline={trace.timeline ?? []} />
        </CardContent>
      </Card>

      {/* CW-Events getrennt von der Timeline rendern (Trap §8.4) */}
      <CloudWatchPanel
        enabled={cwActive}
        isLoading={isCwLoading}
        error={cwError}
        trace={cwTrace}
        onLoad={() => setCwRequested(true)}
        onReload={() => void refetchCw()}
      />
    </div>
  );
}
